import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { exportConfigByTable } from "@/config/dashboard/export";
import { extractLabel } from "@/config/forms/advanced-search/advanced-search-fields-def";
import {
  ExportConfig,
  ExportField,
  ExportGroup,
} from "@/lib/auth/export/export-types";
import { TabMenuItems } from "@/lib/types";
import { cn, isInSearch } from "@/lib/utils";
import { forwardRef, useRef } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

export const ItemGroup = forwardRef<
  HTMLDivElement,
  {
    label: string;
    children: React.ReactNode;
    onChange: (checked: boolean) => void;
    checked: boolean;
    isEmpty: boolean;
  }
>(({ label, children, onChange, checked, isEmpty }, ref) => {
  const { t } = useTranslation();

  return (
    <div ref={ref} className={cn("flex flex-col gap-8", isEmpty && "hidden")}>
      <div className="flex flex-col gap-5">
        <div className="flex items-center justify-between">
          <p className="text-lg font-semibold">{label}</p>
          <div className="flex items-center gap-2">
            <Checkbox checked={checked} onCheckedChange={onChange} />
            <p className="text-sm font-medium text-gray-700">
              {t("selectAll")}
            </p>
          </div>
        </div>
        <Separator />
      </div>
      <div className="flex flex-col gap-5">{children}</div>
    </div>
  );
});

ItemGroup.displayName = "ItemGroup";

type ItemProps =
  | {
      soloItem: true;
      field: ExportField;
      search: string;
      index_i: number;
    }
  | {
      soloItem: false;
      field: ExportField;
      search: string;
      index_i: number;
      index_j: number;
    };

export const Item = (props: ItemProps) => {
  const { t } = useTranslation();
  const { control } = useFormContext<ExportConfig>();
  const { soloItem, field, search, index_i } = props;

  return (
    <div
      key={field.label}
      className={cn(
        "flex items-center gap-6",
        !isInSearch(search, t(extractLabel(field))) && "hidden",
      )}
    >
      <Controller
        control={control}
        name={
          soloItem
            ? `${index_i}.checked`
            : `${index_i}.fields.${props.index_j}.checked`
        }
        render={({ field: { value, onChange } }) => (
          <Checkbox checked={!!value} onCheckedChange={onChange} />
        )}
      />
      <p className="text-sm font-medium text-gray-700">
        {t(extractLabel(field))}
      </p>
    </div>
  );
};

type GroupSectionProps = {
  index: number;
  elem: ExportGroup;
  registerRef: (el: HTMLDivElement | null) => void;
  search: string;
};

export const GroupSection = ({
  index,
  elem,
  registerRef,
  search,
}: GroupSectionProps) => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<ExportConfig>();
  const watchedFields = useWatch({ control, name: `${index}.fields` });

  const isAllChecked = Array.isArray(watchedFields)
    ? watchedFields.every(
        (f) => !!f?.checked || !isInSearch(search, t(extractLabel(f))),
      )
    : false;

  const onToggleAll = (checked: boolean) => {
    elem.fields.forEach((field, j) => {
      if (!isInSearch(search, t(extractLabel(field)))) return;
      setValue(`${index}.fields.${j}.checked`, checked, {
        shouldDirty: true,
      });
    });
  };

  const filteredFields = elem.fields.filter((field) => {
    return isInSearch(search, t(extractLabel(field)));
  });

  return (
    <ItemGroup
      ref={registerRef}
      label={t(elem.label)}
      checked={isAllChecked}
      onChange={onToggleAll}
      isEmpty={filteredFields.length === 0}
    >
      {elem.fields.map((field, j) => (
        <Item
          key={field.key + field.label}
          field={field}
          search={search}
          index_i={index}
          index_j={j}
          soloItem={false}
        />
      ))}
    </ItemGroup>
  );
};

export const ExportFieldsList = ({
  selectedTable,
  search,
  groupRefs,
  setActiveGroup,
}: {
  selectedTable: TabMenuItems;
  search: string;
  groupRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
  setActiveGroup: (group: string | null) => void;
}) => {
  const config = exportConfigByTable[selectedTable];

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    let closestKey: string | null = null;
    let minDistance = Number.POSITIVE_INFINITY;

    const maxScroll = container.scrollHeight - container.clientHeight;
    const scrollRatio = container.scrollTop / maxScroll;
    const distanceBaseOffset = scrollRatio * container.clientHeight;

    Object.entries(groupRefs.current).forEach(([key, el]) => {
      if (!el) return;
      const distance = Math.abs(
        el.offsetTop -
          container.offsetTop -
          container.scrollTop -
          distanceBaseOffset,
      );
      const isAboveBottom =
        el.offsetTop -
          container.offsetTop -
          container.scrollTop -
          container.clientHeight +
          15 <
        0;
      if (distance < minDistance && isAboveBottom) {
        minDistance = distance;
        closestKey = key;
      }
    });

    setActiveGroup(closestKey);
  };

  return (
    <div
      ref={scrollContainerRef}
      onScroll={handleScroll}
      className="flex flex-col gap-16 px-8 max-h-[50vh] overflow-y-auto"
    >
      {config.map((elem, index) => {
        if (elem.type !== "group") {
          return (
            <Item
              key={elem.key + elem.label}
              field={elem}
              search={search}
              index_i={index}
              soloItem={true}
            />
          );
        }
        return (
          <GroupSection
            key={elem.label}
            index={index}
            elem={elem}
            registerRef={(el) => (groupRefs.current[elem.label] = el)}
            search={search}
          />
        );
      })}
    </div>
  );
};
